import React from 'react';

const Disclaimer: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <img 
              src="/clawbrand_logo_color.png" 
              alt="ClawBrand AI" 
              className="h-16 mx-auto mb-6"
            />
            <h1 className="text-4xl font-bold mb-4">Disclaimer</h1>
            <p className="text-gray-300">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">General Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              The information provided by BrandScapes AI ("we," "us," or "our") on our website and through our 
              AI-powered mockup generation service is for general informational purposes only. All information 
              on the site and service is provided in good faith; however, we make no representation or warranty 
              of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, 
              availability, or completeness of any information on the site or service.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Important Notice</h3>
              <p className="text-gray-700">
                Under no circumstance shall we have any liability to you for any loss or damage of any kind 
                incurred as a result of the use of the site or reliance on any information provided on the site. 
                Your use of the site and your reliance on any information on the site is solely at your own risk.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">External Links Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              The BrandScapes AI website may contain (or you may be sent through the site) links to other 
              websites or content belonging to or originating from third parties or links to websites and 
              features in banners or other advertising. Such external links are not investigated, monitored, 
              or checked for accuracy, adequacy, validity, reliability, availability, or completeness by us.
            </p>
            <p className="text-gray-700 mb-6">
              We do not warrant, endorse, guarantee, or assume responsibility for the accuracy or reliability 
              of any information offered by third-party websites linked through the site or any website or 
              feature linked in any banner or other advertising.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              BrandScapes AI is a tool designed to assist in creating marketing mockups. The service should not 
              be relied upon as a substitute for professional design advice, consultation, or services. The 
              mockups generated are based on AI technology and templates, and while we strive for quality, 
              individual results may vary based on input materials and specific use cases.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Testimonials Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              The testimonials and examples of income or business results featured on BrandScapes AI are 
              exceptional results and are not intended to represent or guarantee that anyone will achieve 
              the same or similar results. Each individual's success depends on their background, dedication, 
              desire, and motivation.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Results May Vary</h3>
              <p className="text-gray-700">
                Success in marketing and business depends on many factors beyond the tools used. BrandScapes AI 
                is a tool that can help create professional mockups, but success in your business depends on 
                many other factors including your products, services, marketing strategy, and market conditions.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Fair Use Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              The mockup templates and AI-generated content provided by BrandScapes AI are intended for use in 
              marketing and promotional materials. Users are responsible for ensuring their use of generated 
              mockups complies with all applicable laws, including intellectual property rights and advertising 
              standards in their jurisdiction.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Technology Disclaimer</h2>
            <p className="text-gray-700 mb-4">
              BrandScapes AI utilizes artificial intelligence and machine learning technologies. While we 
              continuously work to improve our algorithms and services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>AI-generated results may occasionally contain imperfections or unexpected outcomes</li>
              <li>The quality of output depends significantly on the quality of input materials</li>
              <li>We cannot guarantee that the service will meet all specific requirements or expectations</li>
              <li>Technology limitations may affect the final output in some cases</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Errors and Omissions Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              While we have made every attempt to ensure that the information contained in this site and service 
              has been obtained from reliable sources, BrandScapes AI is not responsible for any errors or 
              omissions, or for the results obtained from the use of this information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Guarantee of Specific Outcomes</h2>
            <p className="text-gray-700 mb-6">
              BrandScapes AI makes no guarantees regarding specific business outcomes, conversion rates, sales 
              increases, or any other metrics as a result of using our mockup generation service. The effectiveness 
              of marketing materials depends on numerous factors beyond the visual presentation.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">"Use at Your Own Risk" Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              All information provided by BrandScapes AI is provided "as is" with all faults and without warranty 
              of any kind. You acknowledge that you use the service at your own risk and that we shall not be 
              liable for any damages of any kind related to your use of this service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions or concerns about this disclaimer, please contact us:
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
              <p className="text-gray-700">
                <strong>Email:</strong> support@brandscapesai.com<br />
                <strong>Website:</strong> Contact form on our website
              </p>
            </div>

            <div className="mt-12 p-6 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                This disclaimer was last updated on {new Date().toLocaleDateString()}. We reserve the right to 
                update or change our disclaimer at any time, and you should check this page periodically.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-16">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-sm">
              © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Disclaimer;
